import { FRAMES, GRID_FRAMES, pixelType } from "odyssey-shared";
import { KIT_METRICS } from "../../../ui/kit/metrics.ts";

/**
 * Where the interaction prompt's parts go (P1.9-T17, ADR-061 and its amendments): the corner bracket around the
 * target's art, and over it, or under it, one row holding the E key cap and the verb's label. The key cap is the
 * kit's key glyph (`KeyGlyph`): the key frame, painted on the button's insets, around the key's legend with the key's
 * padding across, 18 logical px tall, as the HUD's Esc glyph is built; the label follows it `LABEL_GAP` px on, its
 * line's top on the legend's, so the two share a baseline. The row stands `PROMPT_GAP` px beyond the bracket on the
 * side of the thing away from the character (`promptBelow`), so it never covers the character that reads it, and is
 * centred on the art across.
 *
 * A verb list is further rows, each a cap's height and `ROW_GAP` px beyond the one before, outward from the bracket,
 * so the first row never moves when a list grows; E stays the only key, and no number key is assigned.
 *
 * A refused verb (the design's `refused` state, P1.8-T4) adds a reason under the row, `ROW_GAP` px below it: the 8 px
 * close glyph at the key cap's left, centred on the label role's line, and the reason's text `REASON_GAP` px after it.
 * Over the bracket the row rises by the reason's height, so the reason, not the row, stands nearest the bracket, as
 * the design's column puts the reason last.
 *
 * Every position is computed in device pixels from the art's box, itself projected to whole device pixels, and then
 * divided by the UI scale, so every part draws on the device grid at every scale and nothing is snapped to logical
 * pixels, which would make the prompt judder where the UI scale and the world zoom differ (ADR-061, "world-attached
 * UI has a layer and a camera of its own").
 */

/**
 * Logical pixels the bracket stands outside the art on every side, so its inner edge lies clear of the art.
 */
export const BRACKET_OUTSET = 3;

/**
 * Logical pixels between the bracket and the prompt's first row.
 */
export const PROMPT_GAP = 2;

/**
 * Logical pixels between the key cap and the label, the export's prompt gap.
 */
export const LABEL_GAP = 3;

/**
 * Logical pixels between two rows of a verb list.
 */
export const ROW_GAP = 1;

/**
 * Logical pixels between the refusal's close glyph and its text, the design's.
 */
export const REASON_GAP = 2;

/**
 * The refusal's close glyph's size in logical pixels: the atlas's `close-small_8`.
 */
export const REASON_GLYPH = 8;

/**
 * The refusal's line height in logical pixels: the label role's, which its text is set in.
 */
export const REASON_HEIGHT = pixelType("label").lineHeight;

/**
 * The key cap's height in logical pixels: the kit's key glyph, the button's insets around the label role's line.
 */
export const KEY_HEIGHT = KIT_METRICS.key.height;

/**
 * The key frame's insets, the button's, which the legend sits inside.
 */
const KEY_INSETS = FRAMES.button.insets;

/**
 * The bracket's smallest size in logical pixels: its corners, which hold its whole drawing.
 */
const BRACKET_MIN = { width: GRID_FRAMES.bracket.insets.left + GRID_FRAMES.bracket.insets.right, height: GRID_FRAMES.bracket.insets.top + GRID_FRAMES.bracket.insets.bottom } as const;

/**
 * A box in whole device pixels: left and top inclusive, right and bottom exclusive.
 */
export interface DeviceBox {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/**
 * The prompt's parts, written in place so a prompt laid out every frame allocates nothing: the bracket's top left and
 * size, the key cap's, the legend's and the label's top lefts, and a refusal's glyph's and text's top lefts (those of
 * the row's foot when there is none), in logical pixels on whole device pixels; whether the row stands under the
 * bracket; and the row's box in device pixels.
 */
export interface PromptLayout {
  bracketX: number;
  bracketY: number;
  bracketWidth: number;
  bracketHeight: number;
  keyX: number;
  keyY: number;
  legendX: number;
  legendY: number;
  labelX: number;
  labelY: number;
  reasonGlyphX: number;
  reasonGlyphY: number;
  reasonX: number;
  reasonY: number;
  below: boolean;
  rowLeft: number;
  rowTop: number;
  rowWidth: number;
  rowHeight: number;
}

/**
 * A layout to write into.
 *
 * @returns A layout of zeros.
 */
export function newPromptLayout(): PromptLayout {
  return { bracketX: 0, bracketY: 0, bracketWidth: 0, bracketHeight: 0, keyX: 0, keyY: 0, legendX: 0, legendY: 0, labelX: 0, labelY: 0, reasonGlyphX: 0, reasonGlyphY: 0, reasonX: 0, reasonY: 0, below: false, rowLeft: 0, rowTop: 0, rowWidth: 0, rowHeight: 0 };
}

/**
 * The key cap's width for a legend: the key frame's insets and the key's padding either side of it, as the kit's key
 * glyph is laid out.
 *
 * @param legendWidth The legend's width in logical pixels, its advances in the pixel face.
 * @returns The cap's width in logical pixels.
 */
export function keyCapWidth(legendWidth: number): number {
  return KEY_INSETS.left + KIT_METRICS.key.paddingX + legendWidth + KIT_METRICS.key.paddingX + KEY_INSETS.right;
}

/**
 * Whether the prompt's row stands under the thing rather than over it: under it while the character's feet stand
 * above the middle of the thing's art, so the row lies on the side away from the character.
 *
 * @param artTop The art's top, in world pixels.
 * @param artBottom The art's bottom.
 * @param feetY The local character's feet line, in world pixels.
 * @returns True for under.
 */
export function promptBelow(artTop: number, artBottom: number, feetY: number): boolean {
  return 2 * feetY < artTop + artBottom;
}

/**
 * Lays the prompt out around a target's art.
 *
 * @param art The art's box in whole device pixels.
 * @param scale The UI scale.
 * @param keyWidth The key cap's width in logical pixels (`keyCapWidth`).
 * @param labelWidth The label's width in logical pixels.
 * @param below Whether the row stands under the bracket (`promptBelow`).
 * @param row Which row of a verb list: 0 for the first, nearest the bracket.
 * @param out Where to write the layout.
 * @param reason Whether a refusal's reason stands under the row; none by default.
 * @param keyShown Whether the key cap is drawn: with none (a page with no keyboard) the row is the label alone.
 * @returns `out`.
 */
export function promptLayout(art: DeviceBox, scale: number, keyWidth: number, labelWidth: number, below: boolean, row: number, out: PromptLayout, reason = false, keyShown = true): PromptLayout {
  const cap = keyShown ? keyWidth + LABEL_GAP : 0;
  const outset = BRACKET_OUTSET * scale;
  let left = art.left - outset;
  let top = art.top - outset;
  let width = art.right - art.left + 2 * outset;
  let height = art.bottom - art.top + 2 * outset;
  if (width < BRACKET_MIN.width * scale) {
    left -= Math.floor((BRACKET_MIN.width * scale - width) / 2);
    width = BRACKET_MIN.width * scale;
  }
  if (height < BRACKET_MIN.height * scale) {
    top -= Math.floor((BRACKET_MIN.height * scale - height) / 2);
    height = BRACKET_MIN.height * scale;
  }
  const rowWidth = (cap + labelWidth) * scale;
  const rowLeft = art.left + Math.floor((art.right - art.left) / 2) - Math.floor(rowWidth / 2);
  const step = (KEY_HEIGHT + ROW_GAP) * scale;
  const reasonBlock = reason ? (ROW_GAP + REASON_HEIGHT) * scale : 0;
  const rowTop = below ? top + height + PROMPT_GAP * scale + row * step : top - (PROMPT_GAP + KEY_HEIGHT) * scale - reasonBlock - row * step;
  const reasonTop = rowTop + (KEY_HEIGHT + ROW_GAP) * scale;
  out.bracketX = left / scale;
  out.bracketY = top / scale;
  out.bracketWidth = width / scale;
  out.bracketHeight = height / scale;
  out.keyX = rowLeft / scale;
  out.keyY = rowTop / scale;
  out.legendX = (rowLeft + (KEY_INSETS.left + KIT_METRICS.key.paddingX) * scale) / scale;
  out.legendY = (rowTop + KEY_INSETS.top * scale) / scale;
  out.labelX = (rowLeft + cap * scale) / scale;
  out.labelY = out.legendY;
  out.reasonGlyphX = out.keyX;
  out.reasonGlyphY = (reasonTop + Math.floor((REASON_HEIGHT - REASON_GLYPH) / 2) * scale) / scale;
  out.reasonX = (rowLeft + (REASON_GLYPH + REASON_GAP) * scale) / scale;
  out.reasonY = reasonTop / scale;
  out.below = below;
  out.rowLeft = rowLeft;
  out.rowTop = rowTop;
  out.rowWidth = rowWidth;
  out.rowHeight = KEY_HEIGHT * scale;
  return out;
}

/**
 * Whether a press lands on the prompt: inside its row, grown by `PROMPT_TOUCH_SLOP` logical pixels each side so a finger
 * finds a label of a few letters, or inside its bracket, which marks the thing the row names.
 *
 * @param layout The laid-out prompt.
 * @param scale The UI scale the layout was made at.
 * @param x The press's x in device pixels.
 * @param y The press's y in device pixels.
 * @returns True when the press is on the prompt.
 */
export function promptHit(layout: PromptLayout, scale: number, x: number, y: number): boolean {
  const slop = PROMPT_TOUCH_SLOP * scale;
  if (x >= layout.rowLeft - slop && x < layout.rowLeft + layout.rowWidth + slop && y >= layout.rowTop - slop && y < layout.rowTop + layout.rowHeight + slop) {
    return true;
  }
  const left = layout.bracketX * scale;
  const top = layout.bracketY * scale;
  return x >= left && x < left + layout.bracketWidth * scale && y >= top && y < top + layout.bracketHeight * scale;
}

/**
 * Logical pixels the prompt's row is grown by for a press, so a finger finds it.
 */
export const PROMPT_TOUCH_SLOP = 4;
